import React from 'react';
import { CheckCircle, Briefcase, Code, Shield, Database, Globe, Bot, GraduationCap } from 'lucide-react';

const About = () => {
  const professionalOpportunities = [
    { icon: GraduationCap, title: 'Career Readiness', desc: 'Portfolio of paid work and professional references' },
    { icon: Briefcase, title: 'Help Desk Support', desc: 'Troubleshooting software/hardware issues' },
    { icon: Globe, title: 'Network Setup & Maintenance', desc: 'Routers, switches, and cybersecurity' },
    { icon: Code, title: 'Website Development', desc: 'WordPress, Wix, or Squarespace sites' },
    { icon: Shield, title: 'Cybersecurity Basics', desc: 'Vulnerability scans and security monitoring' },
    { icon: Database, title: 'Data Management', desc: 'Dataset cleaning and SQL databases' },
    { icon: Bot, title: 'AI-Powered Tools', desc: 'Chatbots and workflow automation' },
  ];

  return (
    <section id="about" className="py-24 px-6 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
          backgroundSize: '40px 40px'
        }}></div>
      </div>

      <div className="max-w-7xl mx-auto relative z-10">
        {/* Main Header */}
        <div className="text-center mb-16">
          <div className="inline-block mb-6">
            <span className="text-cyan-400 font-bold text-sm tracking-wide uppercase px-4 py-2 bg-cyan-400/10 rounded-full border border-cyan-400/20">
              About Us
            </span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold mb-6 leading-tight">
            Empowering Tech Careers Nationwide
          </h2>
        </div>

        {/* Main Description */}
        <div className="max-w-5xl mx-auto mb-16">
          <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10">
            <p className="text-xl leading-relaxed text-gray-300 mb-6">
              The 317 Foundation offers technical and AI training programs to individuals across the US who qualify as an alternative to a traditional college education. Our mission is to provide people, particularly those facing economic challenges, with the advanced technical and AI skills necessary to enter today's high-demand industries.
            </p>
            <div className="grid md:grid-cols-2 gap-8 mb-6">
              <div className="flex items-start gap-4">
                <CheckCircle className="text-cyan-400 flex-shrink-0 mt-1" size={24} />
                <div>
                  <h4 className="font-bold text-white mb-2">Learn & Earn Program</h4>
                  <p className="text-gray-400 text-sm">Students receive monthly payments upon successful completion of each course module during our intensive 6-8 month program.</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <CheckCircle className="text-purple-400 flex-shrink-0 mt-1" size={24} />
                <div>
                  <h4 className="font-bold text-white mb-2">95% Job Placement Success</h4>
                  <p className="text-gray-400 text-sm">We provide comprehensive job placement assistance, achieving an outstanding 95% success rate for our graduates.</p>
                </div>
              </div>
            </div>
            <div className="pt-6 border-t border-white/10">
              <p className="text-gray-300 mb-4">
                <strong className="text-cyan-400">National Eligibility:</strong> Students from across the US are eligible to participate in our programs.
              </p>
              <p className="text-gray-300 text-sm">
                <strong className="text-purple-400">Qualification Process:</strong> An interview will be conducted to evaluate students' qualifications, focusing on their aptitude, personal goals, interests, and foundational computer competencies.
              </p>
            </div>
          </div>
        </div>

        {/* Professional Opportunities */}
        <div className="mb-16">
          <h3 className="text-3xl font-bold text-center mb-4">Professional Opportunities</h3>
          <p className="text-center text-gray-400 mb-10">Upon completion, students will qualify for these in-demand roles:</p>
          
          <div className="grid md:grid-cols-3 lg:grid-cols-4 gap-6">
            {professionalOpportunities.map((opp, index) => {
              const Icon = opp.icon;
              return (
                <div key={index} className="bg-white/5 backdrop-blur-sm rounded-xl p-6 border border-white/10 hover:border-cyan-400/30 transition-all hover:transform hover:-translate-y-1">
                  <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center mb-4">
                    <Icon className="text-white" size={24} />
                  </div>
                  <h4 className="font-bold text-white mb-2 text-sm">{opp.title}</h4>
                  <p className="text-gray-400 text-xs">{opp.desc}</p>
                </div>
              );
            })}
            <div className="bg-gradient-to-br from-purple-500/20 to-pink-500/20 backdrop-blur-sm rounded-xl p-6 border border-purple-400/30">
              <div className="text-center">
                <div className="text-3xl font-bold text-purple-400 mb-2">+10</div>
                <p className="text-white font-semibold mb-2">More Opportunities</p>
                <p className="text-gray-300 text-xs">Cloud services, digital marketing, technical writing, remote IT tutoring & more!</p>
              </div>
            </div>
          </div>
        </div>

        {/* Stats Bar */}
        <div className="grid md:grid-cols-4 gap-6 pt-12 border-t border-gray-700">
          <div className="text-center">
            <div className="text-4xl font-bold text-cyan-400 mb-2">95%</div>
            <div className="text-gray-400 text-sm">Job Placement Rate</div>
          </div>
          <div className="text-center">
            <div className="text-4xl font-bold text-purple-400 mb-2">6-8</div>
            <div className="text-gray-400 text-sm">Months Training</div>
          </div>
          <div className="text-center">
            <div className="text-4xl font-bold text-pink-400 mb-2">100%</div>
            <div className="text-gray-400 text-sm">Tuition-Free</div>
          </div>
          <div className="text-center">
            <div className="text-4xl font-bold text-green-400 mb-2">50 States</div>
            <div className="text-gray-400 text-sm">Nationwide Access</div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;